import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Skeleton,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

// Enhanced animations for StandardBanner
const standardBannerStyles = `
  .standard-banner-container {
    transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .standard-banner-image {
    transition: all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: scale(1);
  }
  .standard-banner-container:hover .standard-banner-image {
    transform: scale(1.03);
  }
  .standard-banner-overlay {
    animation: overlaySlideIn 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  @keyframes overlaySlideIn {
    0% {
      opacity: 0;
      transform: translateX(-30px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

// Inject styles
if (
  typeof document !== "undefined" &&
  !document.querySelector("#standard-banner-animations")
) {
  const style = document.createElement("style");
  style.id = "standard-banner-animations";
  style.textContent = standardBannerStyles;
  document.head.appendChild(style);
}

export default function StandardBanner({ banners, handleCTANavigation }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});

  // Handle image loading state
  const handleImageLoad = (imageUrl) => {
    setImagesLoaded((prev) => ({
      ...prev,
      [imageUrl]: true,
    }));
  };

  // Navigation functions
  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? sortedBanners.length - 1 : prevIndex - 1
    );
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % sortedBanners.length);
  };

  // Sort banners by Banner_Position for proper slider order
  const sortedBanners = banners.sort(
    (a, b) => (a.Banner_Position || 0) - (b.Banner_Position || 0)
  );

  // Auto-rotate through banners if multiple
  useEffect(() => {
    if (sortedBanners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % sortedBanners.length);
    }, 8000); // Change every 8 seconds

    return () => clearInterval(interval);
  }, [sortedBanners.length]);

  if (!banners || banners.length === 0) {
    return null;
  }

  const currentBanner = sortedBanners[currentIndex];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        mt: 6,
        mb: 6,
      }}
      className="standard-banner-container"
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          borderRadius: "12px",
          height: {
            xs: "450px",
            sm: "550px",
            md: "70vh",
            lg: "70vh",
            xl: "70vh",
          },
          overflow: "hidden",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        }}
      >
        {/* Banner Image */}
        <img
          src={process.env.BASEURL + currentBanner.Banner_Image_File_Path}
          alt={currentBanner.Image_Heading_Text || "Banner"}
          className="standard-banner-image"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "brightness(1.05) contrast(1.02)",
          }}
          onLoad={() =>
            handleImageLoad(
              process.env.BASEURL + currentBanner.Banner_Image_File_Path
            )
          }
        />

        {/* Loading overlay */}
        {!imagesLoaded[
          process.env.BASEURL + currentBanner.Banner_Image_File_Path
        ] && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 30, 0.9)"
                  : "rgba(245, 245, 245, 0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              zIndex: 10,
              backdropFilter: "blur(4px)",
            }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{
                borderRadius: "12px",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
              }}
            />
          </Box>
        )}

        {/* Overlay gradient */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)"
                : "linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)",
            zIndex: 1,
          }}
        />

        {/* Content overlay */}
        <Container
          maxWidth="lg"
          className="standard-banner-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            zIndex: 2,
            px: { xs: 3, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: { xs: "100%", md: "60%" } }}>
            {currentBanner.Image_Heading_Text && (
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.8rem",
                    md: "2.2rem",
                    lg: "2.5rem",
                  },
                  lineHeight: 1.2,
                  mb: 2,
                  textShadow: "0 4px 12px rgba(0,0,0,0.7)",
                }}
              >
                {currentBanner.Image_Heading_Text}
              </Typography>
            )}

            {currentBanner.Image_Sub_Heading_Text && (
              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 400,
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                    md: "1.1rem",
                  },
                  lineHeight: 1.4,
                  mb: 2,
                  textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                }}
              >
                {currentBanner.Image_Sub_Heading_Text}
              </Typography>
            )}

            {currentBanner.Image_Text && (
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontSize: {
                    xs: "0.85rem",
                    sm: "0.9rem",
                    md: "1rem",
                  },
                  lineHeight: 1.5,
                  mb: 3,
                  textShadow: "0 2px 6px rgba(0,0,0,0.5)",
                  maxWidth: "80%",
                }}
              >
                {currentBanner.Image_Text}
              </Typography>
            )}

            {/* CTA Button */}
            {currentBanner.Banner_CTA_Text &&
              currentBanner.Banner_CTA_Button_Link && (
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() =>
                    handleCTANavigation(currentBanner.Banner_CTA_Button_Link)
                  }
                  sx={{
                    px: 3,
                    py: 1.2,
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    borderRadius: "50px",
                    textTransform: "none",
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.primary.dark,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                  }}
                >
                  {currentBanner.Banner_CTA_Text}
                </Button>
              )}
          </Box>
        </Container>

        {/* Navigation arrows for multiple banners */}
        {sortedBanners.length > 1 && (
          <>
            {/* Previous arrow */}
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.7)"
                    : "rgba(255, 255, 255, 0.8)",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#fff" : "#333",
                width: 48,
                height: 48,
                backdropFilter: "blur(8px)",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(0, 0, 0, 0.85)"
                      : "rgba(255, 255, 255, 0.95)",
                  transform: "translateY(-50%) scale(1.05)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <ArrowBackIos sx={{ fontSize: 20, ml: 0.5 }} />
            </IconButton>

            {/* Next arrow */}
            <IconButton
              onClick={handleNext}
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.7)"
                    : "rgba(255, 255, 255, 0.8)",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#fff" : "#333",
                width: 48,
                height: 48,
                backdropFilter: "blur(8px)",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(0, 0, 0, 0.85)"
                      : "rgba(255, 255, 255, 0.95)",
                  transform: "translateY(-50%) scale(1.05)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <ArrowForwardIos sx={{ fontSize: 20 }} />
            </IconButton>
          </>
        )}

        {/* Slide indicators for multiple banners */}
        {sortedBanners.length > 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 3,
              display: "flex",
              gap: 1,
            }}
          >
            {sortedBanners.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor:
                    index === currentIndex ? "#fff" : "rgba(255,255,255,0.5)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                }}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
